class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    // Only the 'bar' variant sits outside the header's own box and adds to its
    // visual height. The 'message' variant renders inline in the nav, so it is
    // already part of offsetHeight and must not be counted again. Scoped to the
    // header so the cart footer's bar is never picked up.
    this.freeShippingBar = this.header.querySelector(
      'free-shipping-bar[data-fsb-variant="bar"]'
    );
    if (this.freeShippingBar) {
      this.header.classList.add('has-free-shipping-bar');
    }
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    this.setHeaderHeightHandler = this.setHeaderHeight.bind(this);
    this.mediaQuery = window.matchMedia('(max-width: 990px)');
    this.mediaQuery.addEventListener('change', this.setHeaderHeightHandler);
    
    this.header.classList.remove('shopify-section-header-sticky');

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    // A bar that isn't rendered contributes nothing. Measure its margin only
    // when it actually has a box, or `lg:mt-2` leaks 8px into the height on
    // desktop, where the bar is display:none but still reports a margin.
    const renderedHeight = this.freeShippingBar ? this.freeShippingBar.offsetHeight : 0;
    this.freeShippingBarHeight = renderedHeight
      ? renderedHeight + (parseInt(getComputedStyle(this.freeShippingBar).marginTop) || 0)
      : 0;

    // Three distinct quantities, deliberately kept apart:
    //   navHeight    - how far the header travels when it hides. A free
    //                  shipping bar hangs below the nav, so travelling only
    //                  the nav's height leaves the bar pinned on screen.
    //   headerHeight - the header's full visual height, nav + bar. What things
    //                  docking beneath a revealed header need.
    //   hidden offset- once the header has hidden, the bar is all that is left
    //                  at the top of the viewport, so docking stops at the
    //                  bar's bottom edge rather than at 0.
    const navHeight = this.header.offsetHeight;
    this.headerHeight = `${navHeight + this.freeShippingBarHeight}px`;
    this.hiddenStickyOffset = this.freeShippingBarHeight
      ? `${this.freeShippingBarHeight - 1}px`
      : '0px';

    document.documentElement.style.setProperty(
      '--header-height',
      this.headerHeight
    );
    document.documentElement.style.setProperty(
      '--header-nav-height',
      `${navHeight}px`
    );
    document.documentElement.style.setProperty(
      '--free-shipping-bar-height',
      `${this.freeShippingBarHeight}px`
    );

    // Re-measuring as the breakpoint change handler must not fight whichever
    // state the header is currently in.
    this.setStickyOffset(
      this.header.classList.contains('shopify-section-header-hidden')
        ? this.hiddenStickyOffset
        : this.headerHeight
    );
  }

  // Top offset for elements that dock under the header, such as the sticky
  // PLP filter bar: the header height while revealed; once hidden, the free
  // shipping bar's height minus 1px if one is present, else 0 (see
  // hiddenStickyOffset in setHeaderHeight for why the bar's edge, not 0).
  setStickyOffset(offset) {
    document.documentElement.style.setProperty('--facets-sticky-offset', offset);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
    this.mediaQuery.removeEventListener('change', this.setHeaderHeightHandler);
    this.header.classList.remove('has-free-shipping-bar');
    document.documentElement.style.removeProperty('--facets-sticky-offset');
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop) {
      this.header.classList.remove('shopify-section-header-sticky');
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;

      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop) {
      this.header.classList.add('shopify-section-header-sticky');
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      // Resetowanie nagłówka po przewinięciu na górę strony
      this.header.classList.remove('scrolled-past-header');
      this.header.classList.remove('shopify-section-header-sticky');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;

    this.header.classList.add(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky'
    );
    this.setStickyOffset(this.hiddenStickyOffset);
    this.addFixedShippingBar();
    this.closeMenuDisclosure();

    this.dispatchEvent(new CustomEvent('header-hide'));
  }

  addFixedShippingBar(state=true) {
    if (this.freeShippingBar) {
      if (state) {
        this.header.classList.add('has-free-shipping-bar--fixed');
      } else {
        this.header.classList.remove('has-free-shipping-bar--fixed');
      }
    }
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;

    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
    this.setStickyOffset(this.headerHeight);
    this.addFixedShippingBar(false);
    this.dispatchEvent(new CustomEvent('header-reveal'));
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;

    this.header.classList.remove(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
      'animate'
    );
    this.setStickyOffset(this.headerHeight);
    this.addFixedShippingBar(false);
    this.dispatchEvent(new CustomEvent('header-reveal'));
  }

  closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal =
      this.searchModal || this.header.querySelector('details-modal');
    this.searchModal.close(false);
  }
}

customElements.define('sticky-header', StickyHeader);
